import {APP_BASE_HREF} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import * as cookies from 'js-cookie';
import {BehaviorSubject} from 'rxjs';

const STORAGE_KEY = 'authentication';
const AUTH_TOKEN = 'authToken';

export interface SpringOAuthResponse {
  access_token: string;
  expires_at: number;
}

export interface TokenResponse {
  token?: string;
  expiresAt?: number;
}

export interface Auth {
  type: AuthType;
  token: string;
  expiresAt?: number;
  authSourceUrl?: string;
  masquerade?: boolean;
}

type AuthType = 'oauth' | 'token' | 'timecard';

@Injectable()
export class AuthService {

  user$ = new BehaviorSubject<User | null>(null);
  temporaryUser$ = new BehaviorSubject<User | null>(null);
  refreshingUser$ = new BehaviorSubject<boolean>(false);
  masquerading$ = new BehaviorSubject<boolean>(false);

  private userLoaded = false;
  private auth: Auth;
  private temporaryAuthSourceUrl: string;

  constructor(@Inject(APP_BASE_HREF) private basePath: string) {
    this.readAuth();
  }

  storeOAuthAuthentication(data: SpringOAuthResponse): void {
    this.storeOAuthAuthenticationSession(data, false);
  }

  storeOAuthAuthenticationSession(data: SpringOAuthResponse, masquerade: boolean): void {
    this.auth = {
      type: 'oauth',
      token: data.access_token,
      expiresAt: data.expires_at,
      masquerade: masquerade
    };
    this.storeAuth();
  }

  storeTokenAuthentication(data: TokenResponse): void {
    this.auth = {
      type: 'token',
      token: data.token,
      expiresAt: data.expiresAt || 60 * 60 * 24, // 24 hours
      authSourceUrl: this.getAuthSourceUrl()
    };
    this.temporaryAuthSourceUrl = null;
    this.storeAuth();
  }

  storeTimecardSignerToken(token: string): void {
    this.auth = {
      type: 'timecard',
      token
    };
    this.storeAuth();
  }

  removeAuthentication(): void {
    this.auth = null;
    this.user$.next(null);
    this.temporaryUser$.next(null);
    this.userLoaded = false;
    this.temporaryAuthSourceUrl = null;
    this.storeAuth();
  }

  hasAuthentication(): boolean {
    return this.auth != null || this.temporaryAuthSourceUrl != null;
  }

  isFullyAuthenticated(): boolean {
    return this.hasAuthentication() && this.userLoaded;
  }

  startRefresh(): void {
    this.refreshingUser$.next(true);
  }

  finishRefresh(): void {
    this.refreshingUser$.next(false);
    this.masquerading$.next(this.isMasquerade());
    this.userLoaded = true;
  }

  getHeaders(): {[key: string]: string} {
    if (this.auth) {
      if (this.auth.type === 'oauth') {
        return {Authorization: 'Bearer ' + this.auth.token};
      } else if (this.auth.type === 'token' || this.auth.type === 'timecard') {
        return {Authorization: 'Token ' + this.auth.token};
      }
    }
    return null;
  }

  getToken(): string {
    return this.auth && this.auth.token;
  }

  setAuthSourceUrl(url: string): void {
    this.temporaryAuthSourceUrl = url;
  }

  getAuthSourceUrl(): string {
    if (this.temporaryAuthSourceUrl) {
      return this.temporaryAuthSourceUrl;
    } else if (this.auth && this.auth.authSourceUrl) {
      return this.auth.authSourceUrl;
    }
    return null;
  }

  isMasquerade(): boolean {
    return this.auth && this.auth.masquerade;
  }

  getAuthType(): AuthType | null {
    return this.auth && this.auth.type || null;
  }

  private readAuth(): void {
    try {
      this.auth = JSON.parse(cookies.get(STORAGE_KEY));
    } catch (e) {
      this.auth = null;
    }
  }

  private storeAuth(): void {
    if (this.auth != null) {
      cookies.set(STORAGE_KEY, JSON.stringify(this.auth), {
        expires: 8,
        path: this.basePath.slice(0, -1) || '/' // Remove trailing slash
      });
    } else {
      cookies.remove(STORAGE_KEY, {
        path: this.basePath.slice(0, -1) || '/'
      });

      cookies.remove(AUTH_TOKEN, {path: '/', domain: this.getDomain(), secure: true});
    }
  }

  private getDomain(): string {
    if (window.location.hostname.split('.').length < 3) {
      return window.location.hostname;
    } else {
      return window.location.hostname.split('.').slice(-2).join('.');
    }
  }
}
